import { AlertTriangle, Clock, Eye, Flame } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { Link } from "react-router";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  useSignals,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type { SignalsConfig } from "../types";

/**
 * Settings → Keep an eye on. The dashboard's signal panel is rule-based,
 * not a black box - this page shows exactly what each rule is and lets the
 * workspace tune the thresholds.
 */

const NumberField = ({
  label,
  value,
  min,
  max,
  suffix,
  onChange,
  onBlur,
}: {
  label: string;
  value: number;
  min: number;
  max?: number;
  suffix: string;
  onChange: (v: number) => void;
  onBlur?: () => void;
}) => (
  <label className="flex items-center gap-2 text-sm">
    <span className="text-muted-foreground flex-1">{label}</span>
    <Input
      type="number"
      className="h-8 w-20 text-sm tabular-nums"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(Math.max(min, max ? Math.min(max, n) : n));
      }}
      onBlur={onBlur}
    />
    <span className="text-xs text-muted-foreground w-14">{suffix}</span>
  </label>
);

const SignalSection = ({
  icon: Icon,
  title,
  enabled,
  onToggle,
  rule,
  children,
}: {
  icon: React.ElementType;
  title: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  rule: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Card>
    <CardContent className="p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <p className="font-medium text-sm flex-1">{title}</p>
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>
      <p className="text-xs text-muted-foreground">{rule}</p>
      {enabled && children && (
        <div className="flex flex-col gap-2 border-t border-border pt-2.5 max-w-sm">
          {children}
        </div>
      )}
    </CardContent>
  </Card>
);

export const SignalsSettings = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const signals = useSignals();

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for the on/off switches and on
  // blur for the free-typed threshold numbers. Without this, these
  // thresholds only ever lived in the browser's local store - never
  // actually reaching the database.
  const persist = async (next: SignalsConfig) => {
    try {
      await dataProvider.updateConfiguration({ ...config, signals: next });
    } catch {
      notify("Couldn't save that - you may need admin access to update these thresholds.", {
        type: "error",
      });
    }
  };
  const save = (next: SignalsConfig, opts: { persistNow?: boolean } = {}) => {
    updateConfig({ ...config, signals: next });
    if (opts.persistNow !== false) void persist(next);
  };
  const flushSignals = () => void persist(signals);

  return (
    <div className="flex flex-col gap-4 pb-10">
      <Card>
        <CardContent className="p-4 flex flex-col gap-1.5 text-sm">
          <p className="flex items-center gap-2 font-medium">
            <Eye className="w-4 h-4 text-muted-foreground" /> What this panel is
          </p>
          <p className="text-muted-foreground text-xs">
            "Keep an eye on" sits at the top of the dashboard and surfaces
            three watchlists: contacts heating up, deals going quiet, and
            follow-ups past due. Every item is picked by the rules below -
            nothing is a black box, and each threshold is yours to tune.{" "}
            <Link to="/help/keep-an-eye-on" className="underline hover:no-underline">
              Full guide
            </Link>
          </p>
        </CardContent>
      </Card>

      <SignalSection
        icon={Flame}
        title="Heating up"
        enabled={signals.heatingUp.enabled}
        onToggle={(enabled) =>
          save({ ...signals, heatingUp: { ...signals.heatingUp, enabled } })
        }
        rule={
          <>
            Contacts whose lead score is at or above the threshold and who were
            active recently, ranked by score. The score itself comes from your{" "}
            <Link to="/marketing/workflows" className="underline hover:no-underline">
              lead scoring rules
            </Link>{" "}
            - hover any score badge to see exactly which signals earned the
            points.
          </>
        }
      >
        <NumberField
          label="Minimum lead score"
          value={signals.heatingUp.minScore}
          min={0}
          max={100}
          suffix="/ 100"
          onChange={(minScore) =>
            save(
              { ...signals, heatingUp: { ...signals.heatingUp, minScore } },
              { persistNow: false },
            )
          }
          onBlur={flushSignals}
        />
        <NumberField
          label="Active within the last"
          value={signals.heatingUp.activeDays}
          min={1}
          suffix="days"
          onChange={(activeDays) =>
            save(
              { ...signals, heatingUp: { ...signals.heatingUp, activeDays } },
              { persistNow: false },
            )
          }
          onBlur={flushSignals}
        />
      </SignalSection>

      <SignalSection
        icon={AlertTriangle}
        title="Slipping deals"
        enabled={signals.slipping.enabled}
        onToggle={(enabled) =>
          save({ ...signals, slipping: { ...signals.slipping, enabled } })
        }
        rule="Open deals with no activity for longer than the quiet window, longest-quiet first. Won, lost, and archived deals never appear."
      >
        <NumberField
          label="Quiet for at least"
          value={signals.slipping.quietDays}
          min={1}
          suffix="days"
          onChange={(quietDays) =>
            save(
              { ...signals, slipping: { ...signals.slipping, quietDays } },
              { persistNow: false },
            )
          }
          onBlur={flushSignals}
        />
        <NumberField
          label="Ignore deals under"
          value={signals.slipping.minAmount}
          min={0}
          suffix="USD"
          onChange={(minAmount) =>
            save(
              { ...signals, slipping: { ...signals.slipping, minAmount } },
              { persistNow: false },
            )
          }
          onBlur={flushSignals}
        />
      </SignalSection>

      <SignalSection
        icon={Clock}
        title="Follow-ups overdue"
        enabled={signals.overdue.enabled}
        onToggle={(enabled) =>
          save({ ...signals, overdue: { ...signals.overdue, enabled } })
        }
        rule="Your open tasks past their due date, most overdue first. A grace period keeps same-day slips off the list."
      >
        <NumberField
          label="Grace period"
          value={signals.overdue.graceDays}
          min={0}
          suffix="days"
          onChange={(graceDays) =>
            save(
              { ...signals, overdue: { ...signals.overdue, graceDays } },
              { persistNow: false },
            )
          }
          onBlur={flushSignals}
        />
      </SignalSection>

      <Card>
        <CardContent className="p-4 max-w-sm">
          <NumberField
            label="Items shown per column"
            value={signals.perColumn}
            min={1}
            max={10}
            suffix="items"
            onChange={(perColumn) =>
              save({ ...signals, perColumn }, { persistNow: false })
            }
            onBlur={flushSignals}
          />
        </CardContent>
      </Card>
    </div>
  );
};
